import { Component, OnInit } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { DataGridTemplate } from '../../../../../shared/data-grid/data-grid.template';
import { FormDialogTemplate } from '../../../../../shared/form-dialog/form-dialog.template';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { UserService, RoleService, UserProfileService } from '../../../../mesc/mesc.service';
import { PHFUserService } from '../../../phf.service';
import { AuthenticationService } from '../../../../../auth.service';
import { CgpAlertDialogService } from '../../../../../shared/cgp-alert-dialog/cgp-alert-dialog.service';
import { FilterableService } from '../../../../mesc/filterable-service';
import { takeUntil } from 'rxjs/operators';

const GM_USER_ID_PATTERN = '^(?=.[zZ])([a-zA-Z0-9]){6}$|^([0-9]){9}$';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class UsersComponent extends DataGridTemplate implements OnInit {
  editForm: FormGroup;
  formDialog: FormDialogTemplate;
  dialogTitle: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
  module: any;
  users: FilterableService = new FilterableService();
  filteredRoles: FilterableService = new FilterableService();
  phfRoles: any;
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly userService: UserService,
    private readonly phfUserService: PHFUserService,
    private readonly roleService: RoleService,
    private readonly userProfileService: UserProfileService,
    private readonly authService: AuthenticationService,
    readonly cgpAlertDialog: CgpAlertDialogService
  ) {
    super();
  }

  ngOnInit() {
    this.httpErrorHeader = "User Error";
    this.formDialog = new FormDialogTemplate();
    this.modal = true;
    this.filterable = "menu";
    this.excelColumns = [
      {
        field: 'Gmin',
        title: 'Gmin'
      },
      {
        field: 'FirstName',
        title: 'First Name'
      },
      {
        field: 'LastName',
        title: 'Last Name'
      },
      {
        field: 'Email',
        title: 'Email'
      },
      {
        field: 'Roles',
        title: 'Roles'
      },
      {
        field: 'LastLoginDate',
        title: 'Last Login Date'
      },
    ]
    this.formDialog.setHeight(480);

    this.editForm = this.formBuilder.group({
      Id: [],
      FirstName: [0, Validators.required],
      MiddleName: [],
      LastName: [],
      Name: [],
      Roles: [],
      RoleIds: [0, Validators.required],
      UserType: [],
      EmployeeStatus: [],
      Region: [],
      Email: [],
      Phone: [],
      LastLoginDate: [],
      Gmin: [0, Validators.compose([Validators.required, Validators.pattern(GM_USER_ID_PATTERN)])],
      LastLogIn: [],
      Role: [],
      IsActive: []
    })
    this.formDialog.initilize(this.dialogTitle, this.editForm, this.formDialogTabs);

    this.module = this.authService.getActiveModule().value;
    this.authService.getActiveModule()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(x => {
        this.module = x;
        if (x) {
          this.roleService.read('/GetByModule/' + this.module.Id)
            .then(r => {
              r.map(role => role.RoleName = role.Name || role.RoleName);
              this.phfRoles = r;
              return r;
            });
          this.refresh();
        }
      });


    this.dialogTitle.next('User');
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.dialogTitle.complete();
  }

  read(opts: any): Promise<any> {
    return opts.force && this.module ?
      this.phfUserService.read('?moduleId=' + this.module.Id)
        .then(x => {
          x.map(u => {
            u.Roles = u.Roles.join(', ');
            if(u.LastLoginDate) {
              u.LastLoginDate = new Date(u.LastLoginDate)
            }
            return u;
          });
          this.users.next(x);
          return x;
        }) :
      Promise.resolve(this.users.value);
  }

  create(parentEditDataItem: any): void {
    this.formDialog.isNew = true;
    this.editForm.get('Gmin').enable();
    this.formDialog.next({});
  }

  edit(dataItem: any): void {
    this.formDialog.isNew = false;
    const path = `/${this.module.Id}/${dataItem.Id}`;
    this.userService.read(path)
      .then(x => {
        this.editForm.get('Gmin').disable();
        this.editForm.get('RoleIds').enable();
        this.formDialog.next(x);
        this.handleRoleChange()
      })
  }

  save(dataItem: any, isNew: boolean): Promise<any> {

    dataItem.IsActive = true;
    const response = isNew ? this.userService.save(dataItem, isNew, '/AddUserWithRoles/' + this.module.Id) :
      this.userService.save(dataItem, isNew, '/' + this.module.Id, false)

    return response
      .then(() => {
        if (dataItem.Gmin === this.authService.getUserGMIN()) {
          this.userProfileService.read('');
        }
      })
  }

  delete(dataItem: any): Promise<any> {
    return this.userService.remove(`/${this.module.Id}/${dataItem.Id}`);
  }

  onSearch = (e, control: any) => {
    const ctrl = this.editForm.get(control.name);
    if (ctrl.value) {
      this.userService.getCGPUser(ctrl.value)
        .then(obj => {
          if (obj.FirstName === null) {
            this.cgpAlertDialog.showAlertDialog({
              title: "Error",
              message: 'Entered GMIN or GMID does not exist.',
              alert: 'danger',
              closeLabel: 'Close',
            })
          }
          else {
            const roles = this.editForm.value.RoleIds;
            const c = this.editForm.get(control.name);
            this.editForm.reset(obj);
            this.editForm.get('RoleIds').reset(roles);
            this.editDataItem = obj;
            c.disable();
            c.markAsPristine();
            c.markAsUntouched();
            this.editForm.get('RoleIds').enable();
            this.filteredRoles.next(this.phfRoles);
          }
        });
    }
  }

  onClear = () => {
    this.editForm.reset({});
    this.editForm.get('Gmin').enable();
    this.filteredRoles.next(this.phfRoles);
  }

  handleRoleChange = () => {

    const isRole = true;
    this.authService.isPHFProgramAdmin = !isRole;
    this.authService.isPHFSystemAdmin = !isRole;
    this.authService.isPHFUserAdmin = !isRole;
    this.authService.isPHFUser = !isRole;
    this.authService.isPHFGuest = !isRole;
    const selectedRoleIds = this.editForm.get('RoleIds');
    if (selectedRoleIds.value.length === 0) {
      this.filteredRoles.next(this.phfRoles);
    }
    else {
      const guestRoleObj = this.phfRoles.find(role => role.RoleName === 'Guest');
      const guest = selectedRoleIds.value.filter(x => x === guestRoleObj.Id);

      if (guest.length > 0) {
        const filtered = this.phfRoles.filter(x => x.RoleName === 'Guest');
        this.authService.isPHFGuest = isRole;
        this.filteredRoles.next(filtered);
      }
      else {
        const userAdminRoleObj = this.phfRoles.find(role => role.RoleName === 'User Administrator');
        const sysAdminRoleObj = this.phfRoles.find(role => role.RoleName === 'System Administrator');
        const progAdminRoleObj = this.phfRoles.find(role => role.RoleName === 'Program Administrator');
        const userRoleObj = this.phfRoles.find(role => role.RoleName === 'User');

        const admin = selectedRoleIds.value.filter(x => x === userAdminRoleObj.Id ||
          x === sysAdminRoleObj.Id || x === progAdminRoleObj.Id || x === userRoleObj.Id)

        if (admin.length > 0) {
          let filtered = this.phfRoles.filter(x => x.RoleName !== guestRoleObj.RoleName);

          if (admin.find(x => x === userAdminRoleObj.Id)) {
            this.authService.isPHFUserAdmin = isRole;
          }
          if (admin.find(x => x === progAdminRoleObj.Id)) {
            filtered = this.phfRoles.filter(x => x.RoleName === userAdminRoleObj.RoleName || x.RoleName === progAdminRoleObj.RoleName);
            this.authService.isPHFProgramAdmin = isRole;
          }
          if (admin.find(x => x === sysAdminRoleObj.Id)) {
            filtered = this.phfRoles.filter(x => x.RoleName === userAdminRoleObj.RoleName || x.RoleName === sysAdminRoleObj.RoleName);
            this.authService.isPHFSystemAdmin = isRole;
          }
          if (admin.find(x => x === userRoleObj.Id)) {
            filtered = this.phfRoles.filter(x => x.RoleName === userAdminRoleObj.RoleName || x.RoleName === userRoleObj.RoleName);
            this.authService.isPHFUser = isRole;
          }

          this.filteredRoles.next(filtered);
        }
      }
    }
  }

  columns: any[] = [
    {
      field: 'FirstName',
      title: 'First Name',
      filterable: true,
      sortable: true,
    },
    {
      field: 'LastName',
      title: 'Last Name',
      filterable: true,
      sortable: true,
    },
    {
      field: 'Email',
      title: 'Email',
      template: {
        type: 'email',
      }
    },
    {
      field: 'Roles',
      title: 'Roles',
      width: '*'
    },
    {
      field: 'LastLoginDate',
      title: 'Last Login Date',
      template: {type: 'date'},
    },
  ]


  formControls = [
    {
      type: "person",
      name: "Gmin",
      title: "GMIN/GMID",
      service: this.userService,
      stretch: true,
      search: this.onSearch,
      clear: this.onClear
    },
    {
      type: "multiselect",
      title: "Roles",
      name: "RoleIds",
      textField: "RoleName",
      valueField: "Id",
      filterable: false,
      service: this.filteredRoles,
      onChange: this.handleRoleChange,
      stretch: true
    },
  ]

  formDialogTabs = [
    {
      title: "User",
      selected: true,
      formControls: this.formControls
    },
  ]
}
